import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { leadSchema, getValidationError } from "@/lib/validation";

interface ContactFormProps {
  buttonText: string;
  variant?: "men" | "women";
}

const ContactForm = ({ buttonText, variant = "men" }: ContactFormProps) => {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Honeypot field for bot detection
  const [honeypot, setHoneypot] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Bot detection: if honeypot field is filled, silently reject
    if (honeypot) {
      toast({
        title: "הטופס נשלח בהצלחה!",
        description: "נחזור אליך בהקדם האפשרי",
      });
      return;
    }
    
    // Validate form data with Zod schema
    const validationResult = leadSchema.safeParse({
      name,
      phone,
    });

    if (!validationResult.success) {
      const errorMessage = getValidationError(validationResult);
      toast({
        title: "שגיאה",
        description: errorMessage || "נא לבדוק את הפרטים שהזנת",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    
    const { error } = await supabase
      .from('leads')
      .insert({
        name: validationResult.data.name,
        phone: validationResult.data.phone,
        source_page: variant
      });

    if (error) {
      toast({
        title: "שגיאה",
        description: "אירעה שגיאה בשליחת הטופס. נסה שוב.",
        variant: "destructive",
      });
      setIsSubmitting(false);
      return;
    }
    
    toast({
      title: "הטופס נשלח בהצלחה!",
      description: "נחזור אליך בהקדם האפשרי",
    });
    
    setName("");
    setPhone("");
    setIsSubmitting(false);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 w-full max-w-md mx-auto">
      {/* Honeypot field - hidden from users, bots will fill it */}
      <input
        type="text"
        name="website"
        value={honeypot}
        onChange={(e) => setHoneypot(e.target.value)}
        tabIndex={-1}
        autoComplete="off"
        className="absolute opacity-0 pointer-events-none h-0 w-0"
        aria-hidden="true"
      />
      <div className="space-y-3">
        <div>
          <label htmlFor="contact-name" className="sr-only">שם מלא</label>
          <Input
            id="contact-name"
            type="text"
            placeholder="שם מלא"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="h-14 text-lg bg-cream border-border text-navy placeholder:text-muted-foreground focus:ring-gold focus:border-gold"
            dir="rtl"
            aria-required="true"
          />
        </div>
        <div>
          <label htmlFor="contact-phone" className="sr-only">טלפון</label>
          <Input
            id="contact-phone"
            type="tel"
            placeholder="טלפון"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            className="h-14 text-lg bg-cream border-border text-navy placeholder:text-muted-foreground focus:ring-gold focus:border-gold"
            dir="rtl"
            aria-required="true"
          />
        </div>
      </div>
      <Button 
        type="submit" 
        variant="cta" 
        size="xl" 
        className="w-full"
        disabled={isSubmitting}
      >
        {isSubmitting ? "שולח..." : buttonText}
      </Button>
      <p className="text-sm text-muted-foreground text-center mt-3">
        בשליחת הטופס הנך מאשר/ת את{" "}
        <Link to="/privacy" className="underline hover:text-primary transition-colors">
          מדיניות הפרטיות
        </Link>
      </p>
    </form>
  );
};

export default ContactForm;
